import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  LinearProgress,
  Paper,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import GridOnIcon from '@mui/icons-material/GridOn';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ApiIcon from '@mui/icons-material/Api';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import * as XLSX from 'xlsx';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonateProceedButtonSx,
  harmonateUploadButtonSx,
  harmonateCancelButtonSx,
  harmonateTabsSx,
} from '../../../theme/harmonateStyles';

const UPLOAD_TABS = [
  { key: 'pdf', label: 'Upload PDF', icon: <PictureAsPdfIcon /> },
  { key: 'excel', label: 'Upload Excel', icon: <GridOnIcon />, disabled: true },
  { key: 'ai', label: 'AI Extraction', icon: <SmartToyIcon />, disabled: true },
  { key: 'api', label: 'API Import', icon: <ApiIcon />, disabled: true },
];

const INVOICE_DOC_TYPE_NAME = 'Invoice';
const AP_INVOICE_WORKFLOW_NAME = 'AP Invoice';
const PART_SIZE = 5 * 1024 * 1024;

const API_BASE = process.env.REACT_APP_API_URL;
const BACKEND_BASE = process.env.REACT_APP_BACKEND_URL;

const getAuthToken = () => localStorage.getItem('authToken') || localStorage.getItem('token');

const formatFileSize = (bytes) => {
  if (!bytes) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
};

const createFileEntry = (file) => ({
  id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2, 9)}`,
  file,
  status: 'pending',
  progress: 0,
  error: null,
});

const isAcceptedFile = (file, accept) => {
  if (!accept || !file) return true;
  const allowed = accept.split(',').map((item) => item.trim().toLowerCase()).filter(Boolean);
  const name = (file.name || '').toLowerCase();
  if (allowed.some((ext) => name.endsWith(ext))) return true;
  // Some browsers omit/alter extensions; accept common MIME types too.
  const mime = String(file.type || '').toLowerCase();
  if (allowed.includes('.pdf') && mime === 'application/pdf') return true;
  if (allowed.includes('.xlsx') && mime.includes('spreadsheet')) return true;
  if (allowed.includes('.xls') && mime.includes('excel')) return true;
  if (allowed.includes('.csv') && (mime === 'text/csv' || mime === 'application/vnd.ms-excel')) return true;
  return false;
};

const mapExcelRowToDraft = (rows) => {
  const header = {
    invoiceNumber: '',
    vendorName: '',
    taxId: '',
    invoiceDate: null,
    invoiceDueDate: null,
    currency: 'USD',
    invoiceSubtotal: null,
    invoiceTotalTaxes: null,
    invoiceTotalFees: null,
    invoiceTotalAmountDue: null,
  };
  const lines = [];

  rows.forEach((row) => {
    const normalized = {};
    Object.keys(row).forEach((k) => {
      normalized[k.trim().toLowerCase().replace(/\s+/g, '_')] = row[k];
    });

    if (!header.invoiceNumber && normalized.invoice_number) header.invoiceNumber = String(normalized.invoice_number);
    if (!header.vendorName && normalized.vendor_name) header.vendorName = String(normalized.vendor_name);
    if (!header.taxId && normalized.tax_id) header.taxId = String(normalized.tax_id);
    if (!header.invoiceDate && normalized.invoice_date) header.invoiceDate = String(normalized.invoice_date).slice(0, 10);
    if (!header.invoiceDueDate && normalized.due_date) header.invoiceDueDate = String(normalized.due_date).slice(0, 10);
    if (!header.currency && normalized.currency) header.currency = String(normalized.currency);
    if (header.invoiceSubtotal == null && normalized.subtotal != null) header.invoiceSubtotal = Number(normalized.subtotal);
    if (header.invoiceTotalTaxes == null && normalized.tax_amount != null) header.invoiceTotalTaxes = Number(normalized.tax_amount);
    if (header.invoiceTotalFees == null && (normalized.shipping_freight != null || normalized.shipping != null)) {
      header.invoiceTotalFees = Number(normalized.shipping_freight ?? normalized.shipping);
    }
    if (header.invoiceTotalAmountDue == null && normalized.total_amount != null) {
      header.invoiceTotalAmountDue = Number(normalized.total_amount);
    }

    if (normalized.description || normalized.line_description) {
      lines.push({
        lineItemDescription: String(normalized.description || normalized.line_description || ''),
        quantity: normalized.quantity != null ? Number(normalized.quantity) : null,
        lineItemRate: normalized.unit_price != null ? Number(normalized.unit_price) : null,
        lineItemTotal: normalized.line_total != null ? Number(normalized.line_total) : null,
        productCode: normalized.part_sku_number || normalized.sku || normalized.product_code || '',
      });
    }
  });

  return { header, lines };
};

const uploadPdfToWorkflow = async ({ file, tenantId, docTypeConfig, workflow, businessUnitId, businessUnitName, onProgress }) => {
  const token = getAuthToken();
  if (!token) throw new Error('Authentication token not found. Please log in again.');
  if (!API_BASE) throw new Error('API base URL is not configured.');
  if (!tenantId) throw new Error('Tenant ID is missing.');

  const userId = localStorage.getItem('user_id');
  const fileMeta = {
    name: file.name,
    content_type: file.type || 'application/pdf',
    user_id: userId || null,
    document_type_id: docTypeConfig.documentTypeId,
    workflow_definition_snowflake_id: workflow.workflowDefinitionSnowflakeId,
    additional_details: {
      businessUnitId,
      businessUnitName: businessUnitName || null,
      ingestSource: 'AP_INVOICE_UPLOAD',
    },
  };

  const uowResponse = await fetch(`${API_BASE}/${tenantId}/units-of-work`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      name: `AP Invoice - ${file.name}`,
      documents: [fileMeta],
    }),
  });

  if (!uowResponse.ok) {
    throw new Error(`Failed to create unit of work (${uowResponse.status})`);
  }

  const uowData = await uowResponse.json();
  const fileData = uowData.documents?.find((d) => d.name === file.name);
  if (!fileData) {
    throw new Error('Upload session was created but file metadata was not returned.');
  }

  const totalParts = Math.max(1, Math.ceil(file.size / PART_SIZE));
  const urlsResponse = await fetch(`${API_BASE}/multipart/urls`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      id: fileData.id,
      tenant_id: fileData.tenant_id,
      unit_of_work_id: fileData.unit_of_work_id,
      location: fileData.location,
      upload_id: fileData.upload_id,
      name: file.name,
      content_type: file.type || 'application/pdf',
      total_parts: totalParts,
    }),
  });

  if (!urlsResponse.ok) {
    throw new Error(`Failed to get upload URLs (${urlsResponse.status})`);
  }

  const urlsData = await urlsResponse.json();
  const partUrls = Array.isArray(urlsData?.urls) ? urlsData.urls : [];
  if (!partUrls.length) {
    throw new Error('Upload URLs were not returned by the server.');
  }
  const parts = [];

  for (let i = 0; i < totalParts; i += 1) {
    const start = i * PART_SIZE;
    const end = Math.min(start + PART_SIZE, file.size);
    const partBlob = file.slice(start, end);
    const partNumber = i + 1;
    const urlObj = partUrls.find((p) => p.part_number === partNumber);
    if (!urlObj?.url) throw new Error(`Missing upload URL for part ${partNumber}`);

    const uploadResponse = await fetch(urlObj.url, {
      method: 'PUT',
      body: partBlob,
      headers: { 'Content-Type': file.type || 'application/pdf' },
    });

    if (!uploadResponse.ok) {
      throw new Error(`Failed to upload part ${partNumber}`);
    }

    const etag = uploadResponse.headers.get('ETag')?.replace(/"/g, '');
    if (!etag) throw new Error(`Missing ETag for part ${partNumber}`);
    parts.push({ etag, part_number: partNumber });
    onProgress?.(Math.round(((i + 1) / totalParts) * 100));
  }

  const completeResponse = await fetch(`${API_BASE}/multipart/complete`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      id: fileData.id,
      tenant_id: fileData.tenant_id,
      unit_of_work_id: fileData.unit_of_work_id,
      location: fileData.location,
      upload_id: fileData.upload_id,
      name: file.name,
      content_type: file.type || 'application/pdf',
      parts,
    }),
  });

  if (!completeResponse.ok) {
    throw new Error(`Failed to complete upload (${completeResponse.status})`);
  }

  const completeText = await completeResponse.text();
  return completeText ? JSON.parse(completeText) : { ok: true };
};

const InvoiceUploadDialog = ({
  open,
  onClose,
  tenantId,
  businessUnitId,
  businessUnitName,
  onExcelParsed,
  onUploadComplete,
  showInfo,
  showError,
  showSuccess,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [docTypeConfig, setDocTypeConfig] = useState(null);
  const [workflowConfig, setWorkflowConfig] = useState(null);
  const fileInputRef = useRef(null);

  const currentTab = UPLOAD_TABS[activeTab];
  const hasWorkflowConfig = Boolean(docTypeConfig && workflowConfig);
  const pendingFiles = selectedFiles.filter((entry) => entry.status !== 'success');

  const resetState = () => {
    setError(null);
    setLoading(false);
    setUploading(false);
    setSelectedFiles([]);
  };

  const handleClose = () => {
    if (uploading) return;
    resetState();
    onClose();
  };

  const loadInvoiceWorkflowConfig = useCallback(async () => {
    if (!tenantId) return;
    setLoadingConfig(true);
    setError(null);
    try {
      const token = getAuthToken();
      const docTypesResponse = await fetch(
        `${BACKEND_BASE}conductor/document-type-tenant-configs/tenant/${tenantId}?category=WF`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      if (!docTypesResponse.ok) {
        throw new Error('Failed to load Invoice document type configuration.');
      }
      const docTypes = await docTypesResponse.json();
      const invoiceConfig = (Array.isArray(docTypes) ? docTypes : []).find(
        (item) => (item.documentTypeName || item.name || '').toLowerCase() === INVOICE_DOC_TYPE_NAME.toLowerCase()
      );
      if (!invoiceConfig?.documentTypeId) {
        throw new Error('Invoice document type is not configured for this tenant (category WF).');
      }
      setDocTypeConfig(invoiceConfig);

      const workflowResponse = await fetch(
        `${BACKEND_BASE}conductor/tenant-document-type-workflow-snowflake/tenant/${tenantId}/document-type/${invoiceConfig.documentTypeId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      if (!workflowResponse.ok) {
        throw new Error('Failed to load AP Invoice workflow configuration.');
      }
      const workflows = await workflowResponse.json();
      const apWorkflow = (Array.isArray(workflows) ? workflows : []).find(
        (item) => (item.workflowDefinitionName || '').toLowerCase() === AP_INVOICE_WORKFLOW_NAME.toLowerCase()
      );
      if (!apWorkflow?.workflowDefinitionSnowflakeId) {
        throw new Error('AP Invoice workflow is not mapped to the Invoice document type for this tenant.');
      }
      setWorkflowConfig(apWorkflow);
    } catch (err) {
      setDocTypeConfig(null);
      setWorkflowConfig(null);
      setError(err.message || 'Failed to load upload configuration.');
    } finally {
      setLoadingConfig(false);
    }
  }, [tenantId]);

  useEffect(() => {
    if (open) {
      resetState();
      setActiveTab(0);
      loadInvoiceWorkflowConfig();
    }
  }, [open, loadInvoiceWorkflowConfig]);

  const handleChooseFile = () => {
    fileInputRef.current?.click();
  };

  const addFiles = (incomingFiles) => {
    const accept = currentTab.key === 'excel' ? '.xlsx,.xls,.csv' : '.pdf';
    const nextEntries = Array.from(incomingFiles || [])
      .filter((file) => isAcceptedFile(file, accept))
      .map(createFileEntry);

    if (!nextEntries.length) {
      setError(currentTab.key === 'excel'
        ? 'Please select .xlsx, .xls, or .csv files.'
        : 'Please select PDF files.');
      return;
    }

    setError(null);
    setSelectedFiles((prev) => [...prev, ...nextEntries]);
  };

  const handleFileChange = (event) => {
    // Copy before clearing — FileList is live and emptying value clears it.
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;
    addFiles(files);
  };

  const removeFile = (fileId) => {
    if (uploading) return;
    setSelectedFiles((prev) => prev.filter((entry) => entry.id !== fileId));
  };

  const updateFileEntry = (fileId, patch) => {
    setSelectedFiles((prev) => prev.map((entry) => (
      entry.id === fileId ? { ...entry, ...patch } : entry
    )));
  };

  const handleProcessExcel = async () => {
    const first = selectedFiles[0];
    if (!first?.file) return;
    try {
      setLoading(true);
      setError(null);
      const buffer = await first.file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
      if (!rows.length) throw new Error('Excel file is empty or has no readable rows.');
      const draft = mapExcelRowToDraft(rows);
      onExcelParsed?.(draft);
      handleClose();
    } catch (err) {
      setError(err.message || 'Failed to parse Excel file.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedFiles.length) return;
    if (!businessUnitId) {
      setError('Select a business unit before uploading an invoice.');
      return;
    }
    if (!hasWorkflowConfig) {
      setError('Invoice / AP Invoice workflow configuration is not available.');
      return;
    }

    if (currentTab.key === 'excel') {
      await handleProcessExcel();
      return;
    }

    if (currentTab.key === 'api') {
      showInfo?.('API import endpoint integration is pending configuration.');
      return;
    }

    if (currentTab.key !== 'pdf' && currentTab.key !== 'ai') {
      return;
    }

    const filesToUpload = selectedFiles.filter((entry) => entry.status !== 'success');
    if (!filesToUpload.length) return;

    setUploading(true);
    setError(null);

    let successCount = 0;
    let failureCount = 0;

    try {
      for (const entry of filesToUpload) {
        updateFileEntry(entry.id, { status: 'uploading', progress: 0, error: null });
        try {
          if (!entry.file) {
            throw new Error('Selected file is no longer available. Please re-add it and try again.');
          }
          await uploadPdfToWorkflow({
            file: entry.file,
            tenantId,
            docTypeConfig,
            workflow: workflowConfig,
            businessUnitId,
            businessUnitName,
            onProgress: (progress) => {
              const safeProgress = Number.isFinite(progress) ? Math.min(100, Math.max(0, progress)) : 0;
              updateFileEntry(entry.id, { progress: safeProgress });
            },
          });
          updateFileEntry(entry.id, { status: 'success', progress: 100, error: null });
          successCount += 1;
        } catch (err) {
          failureCount += 1;
          updateFileEntry(entry.id, {
            status: 'error',
            error: err?.message || 'Failed to upload invoice.',
          });
        }
      }

      if (successCount > 0) {
        const message = successCount === 1
          ? 'Invoice uploaded. Processing will continue through the AP Invoice workflow.'
          : `${successCount} invoices uploaded. Each file will be processed independently through the AP Invoice workflow.`;
        showSuccess?.(message);
      }

      if (failureCount > 0) {
        const message = failureCount === 1
          ? '1 invoice failed to upload. Review the file list and try again.'
          : `${failureCount} invoices failed to upload. Review the file list and try again.`;
        setError(message);
        showError?.(message);
        if (successCount > 0) {
          onUploadComplete?.();
        }
      } else if (successCount > 0) {
        setUploading(false);
        resetState();
        onClose();
        // Refresh parent after dialog closes to avoid mid-upload remount races.
        onUploadComplete?.();
        return;
      }
    } catch (err) {
      const message = err?.message || 'Failed to upload invoices.';
      setError(message);
      showError?.(message);
    } finally {
      setUploading(false);
    }
  };

  const accept = currentTab.key === 'excel' ? '.xlsx,.xls,.csv' : '.pdf';
  const uploadLabel = uploading
    ? 'Uploading...'
    : currentTab.key === 'excel'
      ? 'Process'
      : selectedFiles.length > 1
        ? `Upload ${pendingFiles.length || selectedFiles.length} files`
        : 'Upload';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: '20px' } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
          bgcolor: ht.darkBlue,
          color: ht.white,
          fontWeight: 700,
        }}
      >
        Upload Invoice
        <IconButton onClick={handleClose} size="small" disabled={uploading} sx={{ color: ht.white }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Tabs
          value={activeTab}
          onChange={(_, v) => { setActiveTab(v); setError(null); setSelectedFiles([]); }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 2, ...harmonateTabsSx, bgcolor: ht.listBg }}
        >
          {UPLOAD_TABS.map((tab) => (
            <Tab key={tab.key} icon={tab.icon} iconPosition="start" label={tab.label} disabled={tab.disabled} />
          ))}
        </Tabs>

        {!businessUnitId && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Select a business unit on the Invoices screen before uploading.
          </Alert>
        )}
        {loadingConfig && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">Loading Invoice workflow configuration...</Typography>
          </Box>
        )}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box
          onClick={uploading ? undefined : handleChooseFile}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (uploading) return;
            addFiles(Array.from(e.dataTransfer.files || []));
          }}
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          sx={{
            border: `2px dashed ${ht.line}`,
            borderRadius: '20px',
            bgcolor: ht.listBg,
            p: 4,
            textAlign: 'center',
            cursor: uploading ? 'default' : 'pointer',
            mb: 2,
            '&:hover': { bgcolor: uploading ? ht.listBg : ht.secondaryTab, borderColor: ht.blue },
          }}
        >
          <CloudUploadIcon sx={{ fontSize: 48, color: ht.text, mb: 1 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
            Drag your file(s) to start uploading
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            OR
          </Typography>
          <Button
            variant="contained"
            component="label"
            disabled={uploading}
            onClick={(e) => e.stopPropagation()}
            sx={harmonateUploadButtonSx}
          >
            Browse files
            <input
              ref={fileInputRef}
              type="file"
              hidden
              multiple
              accept={accept}
              disabled={uploading}
              onChange={handleFileChange}
            />
          </Button>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            {currentTab.key === 'excel'
              ? 'Supports .xlsx, .xls, .csv — select one or more files'
              : 'Supports .pdf — select one or more invoices'}
          </Typography>
        </Box>

        {selectedFiles.length > 0 && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Selected Files ({selectedFiles.length})
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
              <Chip
                size="small"
                label={`Document Type: ${docTypeConfig?.documentTypeName || INVOICE_DOC_TYPE_NAME}`}
                sx={{ bgcolor: '#fff', border: '1px solid #e0e0e0' }}
              />
              <Chip
                size="small"
                label={`Workflow: ${workflowConfig?.workflowDefinitionName || AP_INVOICE_WORKFLOW_NAME}`}
                sx={{ bgcolor: '#fff', border: '1px solid #e0e0e0' }}
              />
              {businessUnitName && (
                <Chip
                  size="small"
                  label={`Business Unit: ${businessUnitName}`}
                  sx={{ bgcolor: '#fff', border: '1px solid #e0e0e0' }}
                />
              )}
            </Box>

            {selectedFiles.map((entry) => (
              <Paper key={entry.id} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <InsertDriveFileIcon color="primary" />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
                      {entry.file?.name || 'Unknown file'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatFileSize(entry.file?.size)}
                    </Typography>
                    {entry.error && (
                      <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.25 }}>
                        {entry.error}
                      </Typography>
                    )}
                  </Box>

                  {entry.status === 'success' && (
                    <CheckCircleOutlineIcon sx={{ color: '#66BB6A' }} fontSize="small" />
                  )}
                  {entry.status === 'error' && (
                    <ErrorOutlineIcon color="error" fontSize="small" />
                  )}

                  {(entry.status === 'uploading' || (uploading && entry.status === 'pending')) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 110 }}>
                      <LinearProgress
                        variant="determinate"
                        value={entry.status === 'uploading' ? (entry.progress || 0) : 0}
                        sx={{ height: 6, borderRadius: 3, width: 80 }}
                      />
                      <Typography variant="caption" sx={{ minWidth: 32 }}>
                        {entry.status === 'uploading' ? `${entry.progress || 0}%` : '…'}
                      </Typography>
                    </Box>
                  )}

                  <IconButton
                    size="small"
                    onClick={() => removeFile(entry.id)}
                    disabled={uploading || entry.status === 'success'}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Paper>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={uploading} variant="outlined" sx={harmonateCancelButtonSx}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={
            !selectedFiles.length
            || uploading
            || loading
            || loadingConfig
            || !businessUnitId
            || ((currentTab.key === 'pdf' || currentTab.key === 'ai') && !hasWorkflowConfig)
            || (currentTab.key !== 'excel' && !pendingFiles.length)
          }
          sx={harmonateProceedButtonSx}
        >
          {uploadLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvoiceUploadDialog;
